import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import type { CustomerGroup, Customer, Country, Factory, Line, Machine } from '../services/BackendDataService';
import { LineIcon } from './LineIcon';
import { MachineIcon } from './MachineIcon';
import { getCountryFlag } from '../utils/countryFlags';
import { 
  translateCountryName, 
  translateMachineType
} from '../utils/sapMetadataTranslations';
import { useThemeClasses } from '../providers/ThemeProvider';
import { useSAPDataStore } from '../store/SAPDataStore';
import { cx } from '@digitalisation/enlixy-comp';

interface SAPTreeViewProps {
  sapData: Record<string, CustomerGroup>;
  onMachineClick?: (machine: Machine, context: { line: Line; factory: Factory; country: Country; customer: Customer }) => void;
  onLineClick?: (line: Line, context: { factory: Factory; country: Country; customer: Customer }) => void;
  onFactoryClick?: (factory: Factory, context: { country: Country; customer: Customer }) => void;
  expandedNodes: Set<string>;
  onToggleNode: (nodeId: string) => void;
  isExpanded: (nodeId: string) => boolean;
  onExpandAll?: () => void;
  onCollapseAll?: () => void;
  isMobile?: boolean;
}

interface TreeNodeProps {
  children: React.ReactNode;
  label: string;
  icon?: string | React.ReactNode;
  isExpanded: boolean;
  onToggle: () => void;
  level: number;
  count?: number;
  onClick?: () => void;
  isMobile?: boolean;
}

const TreeNode: React.FC<TreeNodeProps> = React.memo(({ 
  children, 
  label, 
  icon, 
  isExpanded, 
  onToggle, 
  level, 
  count, 
  onClick,
  isMobile = false
}) => {
  const indentLevel = isMobile ? level * 12 : level * 20; // Reduce indentation on mobile
  const theme = useThemeClasses();
  
  return (
    <div className="">
      <div 
        className={cx(
          "flex items-center cursor-pointer transition-colors duration-200",
          isMobile ? "p-1.5 text-xs" : "p-2", // Smaller padding and text on mobile
          theme.bgElevated,
          theme.borderPrimary,
          onClick ? `${theme.textPrimary} ${theme.hover}` : theme.textSecondary
        )}
        style={{ paddingLeft: `${12 + indentLevel}px` }}
        onClick={(e) => {
          e.stopPropagation();
          if (onClick) {
            onClick();
          } else {
            onToggle();
          }
        }}
      >
        {/* Only show chevron button for items that have children and navigation */}
        {onClick ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            className={cx(
              "rounded transition-colors",
              isMobile ? "mr-1 p-0.5" : "mr-2 p-1", // Smaller button on mobile
              `${theme.hover} hover:${theme.hoverSecondary}`
            )}
          >
            {isExpanded ? (
              <ChevronDownIcon className={cx(
                theme.textSecondary,
                isMobile ? "w-3 h-3" : "w-4 h-4" // Smaller chevron on mobile
              )} />
            ) : (
              <ChevronRightIcon className={cx(
                theme.textSecondary,
                isMobile ? "w-3 h-3" : "w-4 h-4" // Smaller chevron on mobile
              )} />
            )}
          </button>
        ) : (
          <div className={isMobile ? "mr-1 p-0.5" : "mr-2 p-1"}>
            {isExpanded ? (
              <ChevronDownIcon className={cx(
                theme.textSecondary,
                isMobile ? "w-3 h-3" : "w-4 h-4" // Smaller chevron on mobile
              )} />
            ) : (
              <ChevronRightIcon className={cx(
                theme.textSecondary,
                isMobile ? "w-3 h-3" : "w-4 h-4" // Smaller chevron on mobile
              )} />
            )}
          </div>
        )}
        
        {icon && (
          <div className={cx(
            "flex items-center justify-center",
            isMobile ? "mr-1 w-6 h-6" : "mr-2 w-12 h-12" // Smaller icon on mobile
          )}>
            {typeof icon === 'string' ? (
              <span className={isMobile ? "text-sm" : "text-lg"}>{icon}</span>
            ) : (
              icon
            )}
          </div>
        )}
        
        <span className={cx(
          "flex-1 font-medium",
          isMobile ? "truncate" : "", // Truncate text on mobile to prevent overflow
          theme.textPrimary,
          onClick && `hover:${theme.linkPrimary} cursor-pointer`
        )}>
          {label}
        </span>
        
        {count !== undefined && (
          <span className={cx(
            "rounded-full font-medium",
            isMobile ? "ml-1 px-1.5 py-0.5 text-[10px]" : "ml-2 px-2 py-1 text-xs", // Smaller count badge on mobile
            `${theme.bgTertiary} ${theme.textSecondary}`
          )}>
            {count}
          </span>
        )}
      </div>
      
      {isExpanded && (
        <div className={`ml-4 border-l ${theme.borderTree} ${theme.bgElevated}`}>
          {children}
        </div>
      )}
    </div>
  );
});

TreeNode.displayName = 'TreeNode';

const MachineNode: React.FC<{
  machine: Machine;
  level: number;
  context: { line: Line; factory: Factory; country: Country; customer: Customer };
  onMachineClick?: (machine: Machine, context: any) => void;
  isMobile?: boolean;
}> = React.memo(({ machine, level, context, onMachineClick, isMobile = false }) => {
  const { t, i18n } = useTranslation();
  const theme = useThemeClasses();
  const showOnlyWithData = useSAPDataStore(state => state.showOnlyWithData);
  
  // Memoize the translation to avoid recalculation
  const translatedBautyp = useMemo(() => 
    translateMachineType(machine.bautyp || machine.machineType || '', i18n.language),
    [machine.bautyp, machine.machineType, i18n.language]
  );
  
  return (
    <div 
      className={cx(
        "flex items-center cursor-pointer transition-colors rounded-r-lg border-l",
        isMobile ? "p-2 text-xs" : "p-3",
        `${theme.bgElevated} ${theme.hover} ${theme.borderPrimary}`
      )}
      style={{ paddingLeft: `${isMobile ? 16 + level * 12 : 32 + level * 20}px` }}
      onClick={() => onMachineClick?.(machine, context)}
    >
      <div className={isMobile ? "mr-2 flex-shrink-0" : "mr-3 flex-shrink-0"}>
        <MachineIcon 
          machineId={machine.maschinennummer || machine.machineId}
          machineType={machine.bautyp || machine.machineType}
          size={isMobile ? 60 : 120}
          className={theme.textInfo}
        />
      </div>
      <span className="flex-1 flex flex-col">
        <span className={cx(
          "font-medium",
          isMobile ? "truncate" : "",
          theme.textPrimary
        )}>
          {machine.maschinennummer} - {(machine.bautyp || machine.machineType || translatedBautyp || '').replace(new RegExp(`^${machine.maschinennummer}\\s*[-–]\\s*`), '')}
        </span>
      </span>
      {/* Show No Data badge only for machines without data when showing all machines (not on mobile) */}
      {!isMobile && showOnlyWithData && !machine.datenVerfuegbar && (
        <span className={cx(
          "px-2 py-1 rounded-full text-xs font-medium",
          `${theme.bgError} ${theme.textWhite}`
        )}>
          {t('noData') || 'No Data'}
        </span>
      )}
    </div>
  );
});

MachineNode.displayName = 'MachineNode';

export const SAPTreeView: React.FC<SAPTreeViewProps> = ({ 
  sapData, 
  onMachineClick, 
  onLineClick, 
  onFactoryClick,
  expandedNodes,
  onToggleNode,
  isExpanded,
  onExpandAll,
  onCollapseAll,
  isMobile = false
}) => {
  const { t, i18n } = useTranslation();
  const theme = useThemeClasses();

  // Memoize the getAllNodeIds function
  const getAllNodeIds = useCallback(() => {
    const ids: string[] = [];
    Object.entries(sapData).forEach(([groupKey, customerGroup]) => {
      ids.push(`group-${groupKey}`);
      customerGroup.regionen?.forEach((region) => {
        ids.push(`region-${groupKey}-${region.region}`);
        region.laender?.forEach((country) => {
          ids.push(`country-${groupKey}-${region.region}-${country.land}`);
          country.kunden?.forEach((customer) => {
            ids.push(`customer-${groupKey}-${region.region}-${country.land}-${customer.kundennummer}`);
            customer.werke?.forEach((factory) => {
              ids.push(`factory-${customer.kundennummer}-${factory.werkName}`);
              factory.linien?.forEach((line) => {
                ids.push(`line-${factory.werkName}-${line.linieBK}`);
              });
            });
          });
        });
      });
    });
    return ids;
  }, [sapData]);

  // Optimized expand/collapse handlers
  const handleExpandAll = useCallback(() => {
    if (onExpandAll) {
      onExpandAll();
    } else {
      // Fallback to individual toggle method
      const allIds = getAllNodeIds();
      
      allIds.forEach(id => {
        if (!expandedNodes.has(id)) {
          onToggleNode(id);
        }
      });
    }
  }, [onExpandAll, getAllNodeIds, expandedNodes, onToggleNode]);
  
  const handleCollapseAll = useCallback(() => {
    if (onCollapseAll) {
      onCollapseAll();
    } else {
      // Fallback to individual toggle method
      const currentExpanded = Array.from(expandedNodes);
      
      currentExpanded.forEach(id => {
        if (expandedNodes.has(id)) {
          onToggleNode(id);
        }
      });
    }
  }, [onCollapseAll, expandedNodes, onToggleNode]);

  // Memoize the tree structure to avoid unnecessary re-renders
  const memoizedTreeStructure = useMemo(() => {
    return Object.entries(sapData).map(([groupKey, customerGroup]) => (
      <TreeNode
        key={groupKey}
        label={customerGroup.kundengruppe}
        icon="🏢"
        isExpanded={isExpanded(`group-${groupKey}`)}
        onToggle={() => onToggleNode(`group-${groupKey}`)}
        level={0}
        count={customerGroup.regionen?.length || 0}
        isMobile={isMobile}
      >
        {customerGroup.regionen?.map((region) => (
          <TreeNode
            key={`${groupKey}-${region.region}`}
            label={region.region}
            icon="🌍"
            isExpanded={isExpanded(`region-${groupKey}-${region.region}`)}
            onToggle={() => onToggleNode(`region-${groupKey}-${region.region}`)}
            level={1}
            count={region.laender?.length || 0}
            isMobile={isMobile}
          >
            {region.laender?.map((country) => (
              <TreeNode
                key={`${groupKey}-${region.region}-${country.land}`}
                label={translateCountryName(country.land, i18n.language)}
                icon={getCountryFlag(country.land)}
                isExpanded={isExpanded(`country-${groupKey}-${region.region}-${country.land}`)}
                onToggle={() => onToggleNode(`country-${groupKey}-${region.region}-${country.land}`)}
                level={2}
                count={country.kunden?.length || 0}
                isMobile={isMobile}
              >
                {country.kunden?.map((customer) => (
                  <TreeNode
                    key={`${groupKey}-${region.region}-${country.land}-${customer.kundennummer}`}
                    label={customer.kundenName}
                    icon="👥"
                    isExpanded={isExpanded(`customer-${groupKey}-${region.region}-${country.land}-${customer.kundennummer}`)}
                    onToggle={() => onToggleNode(`customer-${groupKey}-${region.region}-${country.land}-${customer.kundennummer}`)}
                    level={3}
                    count={customer.werke?.length || 0}
                    isMobile={isMobile}
                  >
                    {customer.werke?.map((factory) => (
                      <TreeNode
                        key={`${customer.kundennummer}-${factory.werkName}`}
                        label={factory.werkName}
                        icon="🏭"
                        isExpanded={isExpanded(`factory-${customer.kundennummer}-${factory.werkName}`)}
                        onToggle={() => onToggleNode(`factory-${customer.kundennummer}-${factory.werkName}`)}
                        level={4}
                        count={factory.linien?.length || 0}
                        isMobile={isMobile}
                        onClick={() => {
                          // First expand/collapse the factory node
                          onToggleNode(`factory-${customer.kundennummer}-${factory.werkName}`);
                          // Then call the factory click handler
                          onFactoryClick?.(factory, { country, customer });
                        }}
                      >
                        {factory.linien?.map((line) => {
                          // OEM number is on the line (not on machines)
                          const oemSuffix = line.oemNummer ? ` (OEM: ${line.oemNummer})` : '';
                          const lineLabel = (line.linieNummer && line.linieNummer !== 'undefined' ? `${line.linieNummer} - ${line.linieName.replace(new RegExp(`^${line.linieNummer}\\s*[-–]?\\s*`), '')}` : line.linieName) + oemSuffix;
                          
                          return (
                          <TreeNode
                            key={`${factory.werkName}-${line.linieBK}`}
                            label={lineLabel}
                            icon={<LineIcon machineCount={line.totalMachineCount || line.maschinen?.length || 0} size={isMobile ? 48 : 96} className={theme.textPrimary} />}  
                            isExpanded={isExpanded(`line-${factory.werkName}-${line.linieBK}`)}
                            onToggle={() => onToggleNode(`line-${factory.werkName}-${line.linieBK}`)}
                            level={5}
                            count={line.maschinen?.length || 0}
                            isMobile={isMobile}
                            onClick={() => {
                              // For lines, clicking should navigate (not expand)
                              onLineClick?.(line, { factory, country, customer });
                            }}
                          >
                            {line.maschinen?.map((machine, machineIndex) => {
                              // Create a unique key that's guaranteed to be defined
                              const machineKey = machine.maschineBK || machine.maschinennummer || machine.machineId || `machine-${machineIndex}`;
                              const uniqueKey = `${factory.werkName}-${line.linieBK}-${machineKey}-${machineIndex}`;
                              
                              return (
                                <MachineNode
                                  key={uniqueKey}
                                  machine={machine}
                                  level={6}
                                  context={{ line, factory, country, customer }}
                                  onMachineClick={onMachineClick}
                                  isMobile={isMobile}
                                />
                              );
                            })}
                          </TreeNode>
                        );
                        })}
                      </TreeNode>
                    ))}
                  </TreeNode>
                ))}
              </TreeNode>
            ))}
          </TreeNode>
        ))}
      </TreeNode>
    ));
  }, [sapData, isExpanded, onToggleNode, i18n.language, theme.textPrimary, onFactoryClick, onLineClick, onMachineClick, isMobile]);

  if (Object.keys(sapData).length === 0) {
    return (
      <div className={`flex flex-col items-center justify-center p-8 text-center ${theme.bgSecondary} rounded-lg`}>
        <div className="text-6xl mb-4">🌳</div>
        <h3 className={`text-xl font-semibold mb-2 ${theme.textPrimary}`}>{t('noSAPData') || 'No SAP Data Available'}</h3>
        <p className={theme.textSecondary}>{t('loadSAPData') || 'Please load SAP data to see the tree structure.'}</p>
      </div>
    );
  }

  return (
    <div className={`${theme.bgPrimary} rounded-lg overflow-hidden`}>
      <div className={`flex items-center justify-between p-4 ${theme.bgSecondary} ${theme.borderPrimary} border-b`}>
        <div className={`text-lg font-semibold ${theme.textPrimary}`}>
          {t('List of available assets') || 'SAP Structure'}
        </div>
        <div className="flex gap-2">
          <button
            className={`px-3 py-1 text-sm rounded transition-colors ${theme.bgInfo} ${theme.textWhite} hover:opacity-90`}
            onClick={handleExpandAll}
            type="button"
          >
            {t('expandAll') || 'Expand All'}
          </button>
          <button
            className={`px-3 py-1 text-sm rounded transition-colors ${theme.bgSecondary} ${theme.textSecondary} ${theme.borderPrimary} border hover:${theme.hoverSecondary}`}
            onClick={handleCollapseAll}
            type="button"
          >
            {t('collapseAll') || 'Collapse All'}
          </button>
        </div>
      </div>
      <div className={theme.bgElevated}>
        {memoizedTreeStructure}
      </div>
    </div>
  );
};
